/**
 * SampleProof — proof-of-product marketing section.
 *
 * Shows real Ontario Learning Skills inputs + the actual generated
 * comment, side-by-side, for two students (Maya: mixed-level easy
 * case; Liam: struggling N-level hard case). Skeptical teachers
 * landing from FB ads see the product output BEFORE committing to
 * sign up.
 *
 * Background: 9 days of FB ads, 0 paid conversions (2026-05-24). The
 * funnel mechanics work but teachers don't trust AI-generated report
 * comments until they see them. This section is the trust delta.
 *
 * Why static (not live AI on every pageview):
 *   - Marketing page stays off the Gemini hot path (latency + cost)
 *   - Pre-vetted output — no risk of a flaky generation embarrassing
 *     the page
 *   - If the prompt changes substantially, re-run
 *     scripts/sample-comments.ts and update the static text below
 *
 * Used by:
 *   - frontend/src/pages/landing/HomePage.tsx (`/`)
 *   - frontend/src/pages/landing/LandingPage.tsx (`/ontario/learning-skills`)
 *
 * If/when BC and Manitoba landings get a proof section, they can either
 * import this component as-is (same Ontario LS sample is still a
 * powerful demo) or define their own regional component (BC samples
 * use the Proficiency Scale, MB uses C/U/S/R behaviours).
 *
 * E48 (2026-05-24).
 */

import React from "react";
import { Check } from "lucide-react";

interface ProofExample {
  name: string;
  grade: string;
  pronoun: string;
  blurb: string;
  inputs: {
    skill: string;
    level: "E" | "G" | "S" | "N";
    chips: { label: string; tone: "strength" | "growth" }[];
  }[];
  comment: string;
}

const PROOF_EXAMPLES: ProofExample[] = [
  {
    name: "Maya",
    grade: "3",
    pronoun: "She/Her",
    blurb: "Mixed-level student — strong collaborator, needs to manage time better.",
    inputs: [
      { skill: "Responsibility",   level: "G", chips: [{ label: "Class routine", tone: "strength" }, { label: "Homework", tone: "growth" }] },
      { skill: "Organization",     level: "S", chips: [{ label: "Manages time", tone: "growth" }] },
      { skill: "Independent Work", level: "S", chips: [{ label: "On-task", tone: "growth" }] },
      { skill: "Collaboration",    level: "E", chips: [{ label: "Shares ideas", tone: "strength" }, { label: "Respectful", tone: "strength" }, { label: "Helpful to peers", tone: "strength" }] },
      { skill: "Initiative",       level: "G", chips: [{ label: "Curious", tone: "strength" }] },
      { skill: "Self-Regulation",  level: "S", chips: [{ label: "Focus", tone: "growth" }] },
    ],
    comment: `Maya is an exceptionally collaborative student who consistently shares her ideas and shows great respect toward her peers. She is always ready to be helpful, making her a model for teamwork. She demonstrates a strong curiosity for learning and follows daily class routines with ease. As a next step, Maya is encouraged to continue this momentum by ensuring her homework is completed and returned on time. While she follows routines well, she can work on managing her time and staying on-task during independent work periods. To ensure she is prepared for each lesson, she can focus on keeping her materials tidy. Finally, Maya shows a great interest in new topics; as a next step, she can use strategies to maintain her focus during classroom activities.`,
  },
  {
    name: "Liam",
    grade: "4",
    pronoun: "He/Him",
    blurb: "Struggling student with multiple N's — tests the supportive-but-direct tone.",
    inputs: [
      { skill: "Responsibility",   level: "N", chips: [{ label: "Class routine", tone: "growth" }, { label: "Homework", tone: "growth" }] },
      { skill: "Organization",     level: "N", chips: [{ label: "Manages time", tone: "growth" }, { label: "Keeps materials tidy", tone: "growth" }] },
      { skill: "Independent Work", level: "S", chips: [{ label: "Persistence", tone: "strength" }] },
      { skill: "Collaboration",    level: "G", chips: [{ label: "Helpful to peers", tone: "strength" }, { label: "Respectful", tone: "strength" }] },
      { skill: "Initiative",       level: "S", chips: [{ label: "Curious", tone: "strength" }] },
      { skill: "Self-Regulation",  level: "N", chips: [{ label: "Focus", tone: "growth" }, { label: "Manages emotions", tone: "growth" }] },
    ],
    comment: `Liam is a respectful member of the classroom who is consistently helpful to his peers during group activities and is encouraged to continue sharing his ideas. He demonstrates a natural curiosity for new topics; as a next step, he can use this interest to help him stay on-task. While he shows persistence with difficult tasks, as a next step he can use a visual timer to complete his work on time. To build his responsibility and organization, he is encouraged to use a daily checklist to follow class routines and ensure his homework is returned. He will also benefit from a dedicated time each day to tidy his materials. Liam is working on managing his emotions; a next step is for him to use calming strategies to help him maintain focus and stay regulated throughout the day.`,
  },
];

const LEVEL_STYLES: Record<"E" | "G" | "S" | "N", string> = {
  E: "bg-emerald-100 text-emerald-800",
  G: "bg-brand-100 text-brand-800",
  S: "bg-amber-100 text-amber-800",
  N: "bg-rose-100 text-rose-800",
};

export default function SampleProof() {
  return (
    <section className="px-4 sm:px-6 lg:px-10 py-16 md:py-20 bg-white border-y border-stone-100">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-10">
          <p className="inline-flex items-center gap-2 px-3 py-1 bg-brand-50 border border-brand-100 rounded-full text-brand-800 text-xs font-semibold uppercase tracking-[0.12em]">
            See it work
          </p>
          <h2 className="mt-5 text-3xl md:text-4xl font-bold text-stone-900">
            From six clicks to a finished comment.
          </h2>
          <p className="mt-3 text-stone-600 max-w-2xl mx-auto leading-relaxed">
            Mark each student's level on the six Ontario Learning Skills, tag the
            chips that match what you saw, and TeacherDashboard writes the
            comment. No prompting, no editing-from-scratch, no AI tone.
          </p>
        </div>

        <div className="space-y-10">
          {PROOF_EXAMPLES.map((ex) => (
            <div key={ex.name}>
              <p className="text-center text-sm text-stone-600 mb-5">
                <span className="font-semibold text-stone-800">
                  {ex.name}, Grade {ex.grade}, {ex.pronoun}
                </span>
                {" — "}
                {ex.blurb}
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                {/* Inputs card */}
                <div className="rounded-2xl bg-stone-50 border border-stone-200 p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-5">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-stone-500">
                        Inputs
                      </p>
                      <p className="mt-1 text-stone-900 font-semibold">
                        {ex.name} · Grade {ex.grade} · {ex.pronoun}
                      </p>
                    </div>
                    <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs font-medium rounded">
                      Ontario LS
                    </span>
                  </div>
                  <ul className="space-y-3 text-sm">
                    {ex.inputs.map(({ skill, level, chips }) => (
                      <li key={skill} className="flex items-start justify-between gap-3">
                        <span className="text-stone-700 font-medium flex-shrink-0 w-32">
                          {skill}
                        </span>
                        <span className="flex items-center gap-2 flex-1 flex-wrap justify-end">
                          <span
                            className={`inline-flex items-center justify-center w-7 h-7 rounded-md text-xs font-bold ${LEVEL_STYLES[level]}`}
                          >
                            {level}
                          </span>
                          {chips.map((c) => (
                            <span
                              key={c.label}
                              className={`text-[11px] px-2 py-0.5 rounded-full ${
                                c.tone === "strength"
                                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                  : "bg-amber-50 text-amber-700 border border-amber-200"
                              }`}
                            >
                              {c.tone === "strength" ? "+" : "−"} {c.label}
                            </span>
                          ))}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Output card */}
                <div className="rounded-2xl bg-white border border-brand-100 p-6 shadow-md">
                  <div className="flex items-center justify-between mb-5">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-brand-700">
                        Generated comment
                      </p>
                      <p className="mt-1 text-stone-900 font-semibold">
                        ~9 seconds, no editing
                      </p>
                    </div>
                    <Check className="w-5 h-5 text-emerald-600" aria-hidden="true" />
                  </div>
                  <p className="font-serif text-stone-800 text-[15px] leading-relaxed whitespace-pre-line">
                    {ex.comment}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <p className="mt-10 text-center text-sm text-stone-500">
          Real comments, real prompt — Gemini 3 Flash, our calibrated Ontario LS pipeline. Run it on your own students inside the dashboard.
        </p>
      </div>
    </section>
  );
}
